import { Signal, computed } from '@angular/core';
import { HdChartHoverHighlightService } from './hd-chart-hover-highlight.service';

/**
 * Hover-highlightstaat van één poort-element aan de grid-kant: planeetgrid-blokken
 * (uitgebreid en simpel), just-now-cellen en de activatie-kaartjes van het
 * basis-paneel. Eén plek voor de regels (docs/human-design/hd-chart-hover-highlight.md),
 * zodat die elementen zich altijd identiek gedragen; de visuele vertaling
 * (outline, rand, dim-opacity) blijft per component.
 */
export interface HdGateHoverHighlightState {
  /** De poort zit in de effectieve hover/freeze-state, of haar center wordt gehoverd/bevroren. */
  readonly isMatch: Signal<boolean>;
  /** Dit element is zelf de directe hover-bron (gehoverd én bron = grid-blok). */
  readonly isSource: Signal<boolean>;
  /** Niet-matchend tijdens een actieve hover, zolang de grid-kant dimt (dimmen bij hover / één kant). */
  readonly isDimmed: Signal<boolean>;
}

/**
 * @param gate poort van het element; `null` = doet niet mee (geen activatie,
 *   of een element dat de hover-koppeling bewust uit heeft).
 * @param hostHovered of de host zelf momenteel gehoverd wordt (er kunnen meerdere
 *   elementen met dezelfde poort zijn; slechts één is de directe bron).
 */
export function createHdGateHoverHighlight(
  service: HdChartHoverHighlightService | null,
  gate: () => number | null,
  hostHovered: Signal<boolean>,
): HdGateHoverHighlightState {
  const isMatch = computed(() => {
    const ownGate = gate();
    if (ownGate == null || !service?.enabled() || !service.isActive()) return false;
    return service.isGateMatchingHover(ownGate);
  });

  const isSource = computed(() => {
    const ownGate = gate();
    if (ownGate == null || !service?.enabled() || !service.isActive()) return false;
    const state = service.state();
    if (!hostHovered() || state?.source !== 'planet-grid-block') return false;
    if (state.kind === 'gate') return state.gate === ownGate;
    if (state.kind === 'gates') return state.gates.has(ownGate);
    return false;
  });

  const isDimmed = computed(() => {
    if (gate() == null || !service?.enabled() || !service.isActive()) return false;
    if (!service.dimsGrid()) return false;
    return !isMatch();
  });

  return { isMatch, isSource, isDimmed };
}
